import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getFieldErrors,
    getFormError,
    getSelectedPayee,
    getStandingOrderIsPosting,
} from 'lib/selectors';
import { useMakeStandingOrder } from 'lib/hooks';
import {
    PAYMENT_STANDING_ORDER_SUCCESS,
    PAYMENT_STANDING_ORDER,
    PAYMENT_STANDING_ORDER_PAYEE,
} from 'constants/shared/routes';
import { hideModal, showModal } from 'actions/modal';
import {
    CONFIRM_AND_SEND_MODAL,
    STANDING_ORDER_FEE_MODAL,
} from 'constants/shared/modalTypes';
import { dispatchHideModal } from 'utils/modals';
import PageContainer from 'components/shared/generic/pageContainer/presentational/PageContainer';
import StandingOrderForm from '../presentational/StandingOrderForm';
import SectionTitle from 'components/shared/generic/sectionTitle/presentational/SectionTitle';
import { usePrevious } from 'lib/utils';
import {
    REACT_APP_SUITSME_SORTCODE_IDT,
    REACT_APP_SUITSME_SORTCODE_EPS,
} from 'config';
import { trackCustomerIOEvent } from 'utils/customerIO';
import dayjs from 'dayjs';
import { useGetFeesQuery } from 'lib/services/fees';
import { Payee } from 'lib/type/types';
import { CustomerIOEvent } from 'lib/type';

const StandingOrderFormContainer = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const selectedPayee: Payee = useSelector(getSelectedPayee);

    // const isInternal = selectedPayee.isSuitsMeCustomer; we will use this later after be update
    const isInternalPayee =
        selectedPayee.sortCode === REACT_APP_SUITSME_SORTCODE_IDT ||
        selectedPayee.sortCode === REACT_APP_SUITSME_SORTCODE_EPS;

    const { data: allFees } = useGetFeesQuery();
    const {
        standingOrderSetupFee,
        internalTransactionFee,
        externalTransactionFee,
    } = allFees || {};
    const isFeeExempt = isInternalPayee || standingOrderSetupFee === 0;

    const handleClose = useCallback(() => dispatch(hideModal()), [dispatch]);

    const fieldErrors = useSelector(getFieldErrors);
    const formError = useSelector(getFormError);

    const prevState = usePrevious({ fieldErrors, formError });

    useEffect(() => {
        const isNewFormError = formError && !prevState.formError;
        const isNewFieldErrors =
            Object.keys(fieldErrors as object)?.length >
            Object.keys(prevState.fieldErrors as object).length;

        if (isNewFormError || isNewFieldErrors) {
            handleClose();
        }
    }, [fieldErrors, formError, prevState, handleClose]);

    const {
        form,
        fields,
        handleChange,
        handleSubmit,
        isPosting,
        error,
    } = useMakeStandingOrder(onSubmit);

    const handleConfirmModal = () => {
        if (isFeeExempt) {
            return handleShowStandingOrderConfirmModal();
        }
        dispatch(
            showModal({
                type: STANDING_ORDER_FEE_MODAL,
                props: {
                    fee: standingOrderSetupFee,
                    recurranceFee: isInternalPayee
                        ? internalTransactionFee
                        : externalTransactionFee,
                    handleSubmit: handleShowStandingOrderConfirmModal,
                    closeModal,
                    isInternalPayee,
                },
            }),
        );
    };

    const handleShowStandingOrderConfirmModal = () => {
        dispatch(
            showModal({
                type: CONFIRM_AND_SEND_MODAL,
                props: {
                    form,
                    selectedPayee,
                    postingSelector: getStandingOrderIsPosting,
                    error,
                    type: 'standing-order',
                    handleSubmit,
                },
            }),
        );
    };

    const closeModal = useCallback(() => {
        dispatch(hideModal());
    }, [dispatch]);

    function onSubmit() {
        const startOn = form.startOn
            ? dayjs(form.startOn).format('MM/DD/YYYY')
            : null;
        const endOn = form.endOn
            ? dayjs(form.endOn).format('MM/DD/YYYY')
            : null;
        const formattedAmount = Number.parseInt((form.amount * 100).toFixed(0));
        dispatchHideModal(dispatch);
        history.push(PAYMENT_STANDING_ORDER_SUCCESS, {
            data: {
                amount: form.amount,
                recipient: selectedPayee.payeeName,
            },
        });
        trackCustomerIOEvent(CustomerIOEvent.STANDING_ORDER, {
            payeeName: selectedPayee.payeeName,
            amount: -formattedAmount,
            startOn,
            endOn,
        });
    }

    useEffect(() => {
        if (!selectedPayee) {
            history.push(PAYMENT_STANDING_ORDER);
        }
    }, [history, selectedPayee]);

    return (
        <PageContainer className="standing-order-form-container">
            <SectionTitle
                title={selectedPayee?.payeeName}
                backRoute={PAYMENT_STANDING_ORDER_PAYEE}
                additionalText={`${selectedPayee?.accountNo} | ${selectedPayee?.sortCode}`}
            />
            <StandingOrderForm
                {...form}
                fields={fields}
                handleChange={handleChange}
                handleSubmit={handleConfirmModal}
                isPosting={isPosting}
            />
        </PageContainer>
    );
};

export default StandingOrderFormContainer;
